// Association Audit Feed drawer — Step 10.5 (Dave 2026-02-17).
//
// Screen-wide (85 vw) drawer showing every audit event touching this
// association across membership / element / scenario / plan year /
// association-core mutations. Same permission gate as the rest of the
// association surface — Platform / Firm on the tenant / Association
// Admin on this aid can open it.
//
// Design: mirrors `AssociationMembersDrawer` chrome (navy header,
// coral-2-navy-left-border shell) but wider since the audit rows carry
// more content than a member row.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, RotateCw, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-friendly labels for the entity_type column. Undocumented types
// pass through as-is so we never lose a row.
const ENTITY_LABEL = {
  membership: "Member access",
  element: "Element",
  scenario: "Scenario",
  plan_year: "Plan year",
  association: "Association",
};

// Compact per-entity color chip (uses existing palette vars). Same set
// of colors as the Admin -> Audit table where already established.
const ENTITY_COLOR = {
  membership: "#0091D5", // sky
  element: "#1C4E80",    // navy
  scenario: "#EA6A47",   // coral
  plan_year: "#EA6A47",  // coral
  association: "#1C4E80",// navy
};

const ACTION_LABEL = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  soft_delete: "Deleted",
  restore: "Restored",
  reassign: "Reassigned",
  patch: "Updated",
};

export default function AuditFeedDrawer({ association, onClose }) {
  const aid = association?.aid;
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true); // 2026-10-08 (Dave) — start true so the first paint shows the loader instead of flashing the empty state before the fetch kicks off
  const [expanded, setExpanded] = useState({}); // event_id -> bool

  // Filters: which entity types to show.
  const [showTypes, setShowTypes] = useState({
    membership: true,
    element: true,
    scenario: true,
    plan_year: true,
    association: true,
  });
  // 2026-02-17 (Board President UX) — "what changed since our last
  // meeting?" toggle. "7d" / "30d" / "all". Client-side cut against
  // the same rows we already fetched (limit=200 upstream), so the
  // toggle is instant.
  const [dateRange, setDateRange] = useState("all");

  // 2026-08-17 (Dave) — column sorting. Default: newest first ("when" desc).
  // Clicking the same header toggles direction; clicking a different header
  // resets to descending for that column.
  const [sortKey, setSortKey] = useState("when");   // "when" | "type"
  const [sortDir, setSortDir] = useState("desc");   // "asc"  | "desc"
  const changeSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/associations/${aid}/audit?limit=200`);
      setRows(res.data?.events || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load audit feed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (aid) load();
  }, [aid]);

  const filtered = useMemo(() => {
    let out = rows.filter((r) => showTypes[r.entity_type] !== false);
    if (dateRange !== "all") {
      const days = dateRange === "7d" ? 7 : 30;
      const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
      out = out.filter((r) => {
        if (!r.at) return false;
        return new Date(r.at).getTime() >= cutoffMs;
      });
    }
    // 2026-08-17 sort — stable, keeps rendering deterministic across
    // re-filters. "when" compares millis; "type" compares the
    // human-friendly label so "Association" sorts near "Element" etc.
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      if (sortKey === "when") {
        const ta = a.at ? new Date(a.at).getTime() : 0;
        const tb = b.at ? new Date(b.at).getTime() : 0;
        return (ta - tb) * dir;
      }
      // sortKey === "type"
      const la = ENTITY_LABEL[a.entity_type] || a.entity_type || "";
      const lb = ENTITY_LABEL[b.entity_type] || b.entity_type || "";
      return la.localeCompare(lb) * dir;
    };
    return [...out].sort(cmp);
  }, [rows, showTypes, dateRange, sortKey, sortDir]);

  const toggleType = (t) =>
    setShowTypes((prev) => ({ ...prev, [t]: !prev[t] }));

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(15,42,68,0.35)" }}
      onClick={onClose}
      data-testid="assoc-audit-drawer"
    >
      <div
        className="bg-white h-full w-[1000px] max-w-[95vw] shadow-2xl border-l-2 border-navy flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between bg-navy text-white px-4 py-3">
          <div className="font-semibold text-sm">
            Audit Feed &middot; {association?.name}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              title="Refresh"
              className="text-white/85 hover:text-white disabled:opacity-40"
              data-testid="assoc-audit-refresh"
            >
              <RotateCw size={14} />
            </button>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
              data-testid="assoc-audit-close"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Filter strip */}
        <div className="flex items-center gap-2 flex-wrap px-4 py-2 border-b border-rule bg-[#F5F9FE] text-[11px]">
          <span className="text-slate uppercase tracking-wider text-[10px] font-semibold">
            Show
          </span>
          {Object.entries(ENTITY_LABEL).map(([t, label]) => (
            <button
              key={t}
              onClick={() => toggleType(t)}
              data-testid={`assoc-audit-filter-${t}`}
              className={
                "inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[11px] font-semibold transition-colors " +
                (showTypes[t]
                  ? "border-navy text-white bg-navy"
                  : "border-rule text-slate hover:border-navy hover:text-navy")
              }
              style={
                showTypes[t]
                  ? { boxShadow: `inset 5px 0 0 ${ENTITY_COLOR[t]}` }
                  : undefined
              }
            >
              {label}
            </button>
          ))}

          {/* Divider before the date-range group */}
          <span
            aria-hidden="true"
            className="mx-1"
            style={{ borderLeft: "1px solid var(--u-rule)", height: 16 }}
          />
          <span className="text-slate uppercase tracking-wider text-[10px] font-semibold">
            Since
          </span>
          {[
            { v: "7d",  l: "Last 7 days"  },
            { v: "30d", l: "Last 30 days" },
            { v: "all", l: "All time"     },
          ].map(({ v, l }) => (
            <button
              key={v}
              onClick={() => setDateRange(v)}
              data-testid={`assoc-audit-range-${v}`}
              className={
                "inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[11px] font-semibold transition-colors " +
                (dateRange === v
                  ? "border-coral text-white bg-coral"
                  : "border-rule text-slate hover:border-coral hover:text-coral")
              }
            >
              {l}
            </button>
          ))}

          <span className="ml-auto text-slate">
            {filtered.length} of {rows.length} event{rows.length === 1 ? "" : "s"}
          </span>
        </div>

        {err && (
          <div
            className="mx-4 mt-3 px-3 py-2 border border-coral bg-[#FEF5F2] text-coral text-[12px]"
            data-testid="assoc-audit-error"
          >
            {err}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-4 text-slate italic text-[12px]">
              Loading&hellip;
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="p-4 text-slate italic text-[12px]">
              No audit events match the current filters.
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <table className="w-full text-[12px]" data-testid="assoc-audit-table">
              <thead className="bg-navy text-white sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold" style={{ width: 30 }}></th>
                  <th className="text-left px-3 py-1.5 font-semibold" style={{ width: 155 }}>
                    <button
                      type="button"
                      onClick={() => changeSort("when")}
                      className="inline-flex items-center gap-1 text-white/95 hover:text-white"
                      data-testid="assoc-audit-sort-when"
                    >
                      When
                      {sortKey === "when" ? (
                        sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                      ) : (
                        <ArrowDown size={11} className="opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-3 py-1.5 font-semibold" style={{ width: 130 }}>
                    <button
                      type="button"
                      onClick={() => changeSort("type")}
                      className="inline-flex items-center gap-1 text-white/95 hover:text-white"
                      data-testid="assoc-audit-sort-type"
                    >
                      Type
                      {sortKey === "type" ? (
                        sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                      ) : (
                        <ArrowDown size={11} className="opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-3 py-1.5 font-semibold" style={{ width: 90 }}>Action</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Summary</th>
                  <th className="text-left px-3 py-1.5 font-semibold" style={{ width: 180 }}>By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ev, i) => {
                  const isExp = !!expanded[ev.event_id];
                  const at = ev.at ? new Date(ev.at) : null;
                  return (
                    <React.Fragment key={ev.event_id}>
                      <tr
                        style={{ background: i % 2 === 1 ? "var(--u-sky-wash)" : undefined }}
                        data-testid={`assoc-audit-row-${ev.event_id}`}
                      >
                        <td className="px-3 py-1 border-b border-rule">
                          <button
                            onClick={() =>
                              setExpanded((prev) => ({ ...prev, [ev.event_id]: !isExp }))
                            }
                            className="text-slate hover:text-navy"
                            data-testid={`assoc-audit-toggle-${ev.event_id}`}
                            title={isExp ? "Collapse" : "Expand"}
                          >
                            {isExp ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          </button>
                        </td>
                        <td
                          className="px-3 py-1 border-b border-rule font-mono text-[11px] whitespace-nowrap"
                          title={ev.at || ""}
                        >
                          {at ? at.toLocaleString("en-US", { timeZone: "America/Chicago" }) : "\u2014"}
                        </td>
                        <td className="px-3 py-1 border-b border-rule">
                          <span
                            className="inline-block px-1.5 py-0.5 rounded-[3px] text-[10.5px] font-semibold text-center"
                            style={{
                              background: ENTITY_COLOR[ev.entity_type] || "var(--u-slate)",
                              color: "#fff",
                              minWidth: 96,
                            }}
                          >
                            {ENTITY_LABEL[ev.entity_type] || ev.entity_type}
                          </span>
                        </td>
                        <td className="px-3 py-1 border-b border-rule">
                          {ACTION_LABEL[ev.action] || ev.action}
                        </td>
                        <td className="px-3 py-1 border-b border-rule">
                          {ev.summary || <span className="text-slate italic">&mdash;</span>}
                        </td>
                        <td className="px-3 py-1 border-b border-rule text-slate text-[11.5px]">
                          {ev.actor_full_name || ev.actor_email || "system"}
                        </td>
                      </tr>
                      {isExp && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-2 border-b border-rule"
                            style={{ background: "#F0F6FD" }}
                          >
                            <div className="grid grid-cols-2 gap-3 text-[11.5px]">
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate font-semibold mb-1">
                                  Before
                                </div>
                                <pre className="font-mono text-[10.5px] whitespace-pre-wrap bg-white border border-rule rounded-[3px] p-2 max-h-52 overflow-auto">
                                  {ev.before ? JSON.stringify(ev.before, null, 2) : "\u2014"}
                                </pre>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate font-semibold mb-1">
                                  After
                                </div>
                                <pre className="font-mono text-[10.5px] whitespace-pre-wrap bg-white border border-rule rounded-[3px] p-2 max-h-52 overflow-auto">
                                  {ev.after ? JSON.stringify(ev.after, null, 2) : "\u2014"}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
